<script setup lang="ts">
/**
 * Module: web/src/views/admin/AdminPinsView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Quick-PIN admin surface (doc 04 sect 9.6, gated user_admin): active-PIN
 * list (person, scope, issued by, issued at, expiry, last used) with a
 * two-tap Revoke, plus the issue dialog. pin_hash never reaches this client
 * (server never serializes it); the 4 digits appear exactly once at issuance.
 */
import { computed, onMounted, ref } from 'vue';
import { apiGet, apiPost, unwrapList } from '@/composables/useApi';
import type { AdminPin } from '@/types/api';
import { fmtDateTime } from '@/lib/format';
import PinIssueModal from '@/components/admin/PinIssueModal.vue';
import AppIcon from '@/components/AppIcon.vue';

const pins = ref<AdminPin[]>([]);
const loading = ref(true);
const errorMessage = ref('');
const issueOpen = ref(false);
const armedRevoke = ref<number | null>(null);
const busy = ref(false);

function normalizePin(raw: Record<string, unknown>): AdminPin {
  const scope = raw.scope ?? raw.capability_scope ?? [];
  const person = (raw.person ?? null) as AdminPin['person'];
  return {
    id: Number(raw.id),
    person_id: Number(raw.person_id ?? person?.id ?? 0),
    person_name: typeof raw.person_name === 'string' ? raw.person_name : person?.name,
    person: person ?? undefined,
    scope: Array.isArray(scope) ? (scope as string[]) : [],
    active: raw.active !== false,
    issued_by: (raw.issued_by as AdminPin['issued_by']) ?? null,
    issued_at: typeof raw.issued_at === 'string' ? raw.issued_at : null,
    expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : null,
    last_used_at: typeof raw.last_used_at === 'string' ? raw.last_used_at : null,
  };
}

const activePins = computed(() => pins.value.filter((p) => p.active !== false));

function personLabel(p: AdminPin): string {
  return p.person_name ?? p.person?.name ?? `person #${p.person_id}`;
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const body = await apiGet<unknown>('/admin/pins');
    pins.value = unwrapList<Record<string, unknown>>(body, 'pins').map(normalizePin);
  } catch {
    errorMessage.value = 'Could not load PINs.';
  } finally {
    loading.value = false;
  }
}

onMounted(load);

async function revoke(pin: AdminPin) {
  if (armedRevoke.value !== pin.id) {
    armedRevoke.value = pin.id;
    setTimeout(() => {
      if (armedRevoke.value === pin.id) armedRevoke.value = null;
    }, 4000);
    return;
  }
  armedRevoke.value = null;
  busy.value = true;
  try {
    await apiPost<void>(`/admin/pins/${pin.id}/revoke`);
    await load();
  } catch {
    errorMessage.value = 'Revoke failed.';
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="px-4 py-5 md:p-6 max-w-6xl">
    <div class="flex items-center justify-between gap-3 mb-4">
      <div>
        <h1 class="text-xl font-bold text-fg">Quick PINs</h1>
        <p class="text-sm text-fg-muted">Low-risk quick-access credentials. Scope is hard-limited.</p>
      </div>
      <button class="btn-primary !h-11 !px-4 text-sm" @click="issueOpen = true">Issue Quick PIN</button>
    </div>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>
    <div v-if="loading" class="text-sm text-fg-muted py-8 text-center">Loading…</div>

    <template v-else>
      <div v-if="activePins.length === 0" class="bg-card border border-border-c rounded-lg p-8 text-center">
        <AppIcon name="key" class="w-8 h-8 text-fg-muted mx-auto mb-2" />
        <p class="text-sm text-fg-muted">No active PINs. Issue one to give someone the quick lane.</p>
      </div>

      <template v-else>
        <!-- Mobile: stacked cards -->
        <div class="md:hidden space-y-3">
          <div v-for="p in activePins" :key="p.id" class="bg-card border border-border-c rounded-lg p-4 shadow-card">
            <div class="flex items-start justify-between gap-2">
              <div class="font-semibold text-fg truncate">{{ personLabel(p) }}</div>
              <button
                class="btn-danger !h-10 !px-3 text-sm flex-shrink-0"
                :disabled="busy"
                @click="revoke(p)"
              >
                {{ armedRevoke === p.id ? 'Sure?' : 'Revoke' }}
              </button>
            </div>
            <div class="mt-1.5 flex flex-wrap gap-1">
              <code
                v-for="s in p.scope"
                :key="s"
                class="text-xs px-2 py-0.5 rounded bg-input border border-border-c text-fg-muted"
              >{{ s }}</code>
            </div>
            <dl class="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-fg-muted">
              <dt>Issued</dt><dd class="text-right">{{ fmtDateTime(p.issued_at) }}</dd>
              <dt>Expires</dt><dd class="text-right">{{ p.expires_at ? fmtDateTime(p.expires_at) : 'never' }}</dd>
              <dt>Last used</dt><dd class="text-right">{{ fmtDateTime(p.last_used_at) }}</dd>
              <dt>Issued by</dt><dd class="text-right">{{ p.issued_by ?? '—' }}</dd>
            </dl>
          </div>
        </div>

        <!-- Desktop: table -->
        <div class="hidden md:block bg-card border border-border-c rounded-lg overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase tracking-wide text-fg-muted border-b border-border-c">
                  <th class="px-4 py-3 font-medium">Person</th>
                  <th class="px-4 py-3 font-medium">Scope</th>
                  <th class="px-4 py-3 font-medium">Issued by</th>
                  <th class="px-4 py-3 font-medium">Issued</th>
                  <th class="px-4 py-3 font-medium">Expires</th>
                  <th class="px-4 py-3 font-medium">Last used</th>
                  <th class="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in activePins" :key="p.id" class="border-b border-border-c/60 last:border-0 hover:bg-input/40">
                  <td class="px-4 py-2.5 text-fg font-medium whitespace-nowrap">{{ personLabel(p) }}</td>
                  <td class="px-4 py-2.5">
                    <div class="flex flex-wrap gap-1">
                      <code
                        v-for="s in p.scope"
                        :key="s"
                        class="text-xs px-2 py-0.5 rounded bg-input border border-border-c text-fg-muted"
                      >{{ s }}</code>
                    </div>
                  </td>
                  <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ p.issued_by ?? '—' }}</td>
                  <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ fmtDateTime(p.issued_at) }}</td>
                  <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ p.expires_at ? fmtDateTime(p.expires_at) : 'never' }}</td>
                  <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ fmtDateTime(p.last_used_at) }}</td>
                  <td class="px-4 py-2.5 text-right">
                    <button class="btn-danger !h-9 !px-3 text-xs" :disabled="busy" @click="revoke(p)">
                      {{ armedRevoke === p.id ? 'Confirm?' : 'Revoke' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </template>

    <PinIssueModal :open="issueOpen" @close="issueOpen = false" @issued="load" />
  </div>
</template>
